"use strict";

exports.__esModule = true;
exports.TrackEntry = exports.SUBSEQUENT = exports.SETUP = exports.HOLD_SUBSEQUENT = exports.HOLD_MIX = exports.HOLD_FIRST = exports.FIRST = exports.EventType = exports.EventQueue = exports.CURRENT = exports.AnimationStateAdapter = exports.AnimationState = void 0;

var _Animation = require("./Animation");

var _Utils = require("./Utils");

/******************************************************************************
 * Spine Runtimes License Agreement
 * Last updated January 1, 2020. Replaces all prior versions.
 *
 * Copyright (c) 2013-2020, Esoteric Software LLC
 *
 * Integration of the Spine Runtimes into software or otherwise creating
 * derivative works of the Spine Runtimes is permitted under the terms and
 * conditions of Section 2 of the Spine Editor License Agreement:
 * http://esotericsoftware.com/spine-editor-license
 *
 * Otherwise, it is permitted to integrate the Spine Runtimes into software
 * or otherwise create derivative works of the Spine Runtimes (collectively,
 * "Products"), provided that each user of the Products must obtain their own
 * Spine Editor license and redistribution of the Products in any form must
 * include this license and copyright notice.
 *
 * THE SPINE RUNTIMES ARE PROVIDED BY ESOTERIC SOFTWARE LLC "AS IS" AND ANY
 * EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
 * DISCLAIMED. IN NO EVENT SHALL ESOTERIC SOFTWARE LLC BE LIABLE FOR ANY
 * DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
 * (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES,
 * BUSINESS INTERRUPTION, OR LOSS OF USE, DATA, OR PROFITS) HOWEVER CAUSED AND
 * ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
 * (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF
 * THE SPINE RUNTIMES, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 *****************************************************************************/

/** Applies animations over time, queues animations for later playback, mixes (crossfading) between animations, and applies
 * multiple animations on top of each other (layering).
 *
 * See [Applying Animations](http://esotericsoftware.com/spine-applying-animations/) in the Spine Runtimes Guide. */
var AnimationState = /*#__PURE__*/function () {
  function AnimationState(data) {
    /** The AnimationStateData to look up mix durations. */
    this.data = null;
    /** The list of tracks that currently have animations, which may contain null entries. */

    this.tracks = new Array();
    /** Multiplier for the delta time when the animation state is updated, causing time for all animations and mixes to play slower
     * or faster. Defaults to 1.
     *
     * See TrackEntry {@link TrackEntry#timeScale} for affecting a single animation. */

    this.timeScale = 1;
    this.unkeyedState = 0;
    this.events = new Array();
    this.listeners = new Array();
    this.queue = new EventQueue(this);
    this.propertyIDs = new _Utils.StringSet();
    this.animationsChanged = false;
    this.trackEntryPool = new _Utils.Pool(function () {
      return new TrackEntry();
    });
    this.data = data;
  }

  AnimationState.emptyAnimation = function emptyAnimation() {
    if (!_emptyAnimation) _emptyAnimation = new _Animation.Animation("<empty>", [], 0);
    return _emptyAnimation;
  }
  /** Increments each track entry {@link TrackEntry#trackTime()}, setting queued animations as current if needed. */
  ;

  var _proto = AnimationState.prototype;

  _proto.update = function update(delta) {
    delta *= this.timeScale;
    var tracks = this.tracks;

    for (var i = 0, n = tracks.length; i < n; i++) {
      var current = tracks[i];
      if (!current) continue;
      current.animationLast = current.nextAnimationLast;
      current.trackLast = current.nextTrackLast;
      var currentDelta = delta * current.timeScale;

      if (current.delay > 0) {
        current.delay -= currentDelta;
        if (current.delay > 0) continue;
        currentDelta = -current.delay;
        current.delay = 0;
      }

      var next = current.next;

      if (next) {
        // When the next entry's delay is passed, change to the next entry, preserving leftover time.
        var nextTime = current.trackLast - next.delay;

        if (nextTime >= 0) {
          next.delay = 0;
          next.trackTime += current.timeScale == 0 ? 0 : (nextTime / current.timeScale + delta) * next.timeScale;
          current.trackTime += currentDelta;
          this.setCurrent(i, next, true);

          while (next.mixingFrom) {
            next.mixTime += delta;
            next = next.mixingFrom;
          }

          continue;
        }
      } else if (current.trackLast >= current.trackEnd && !current.mixingFrom) {
        tracks[i] = null;
        this.queue.end(current);
        this.clearNext(current);
        continue;
      }

      if (current.mixingFrom && this.updateMixingFrom(current, delta)) {
        // End mixing from entries once all have completed.
        var from = current.mixingFrom;
        current.mixingFrom = null;
        if (from) from.mixingTo = null;

        while (from) {
          this.queue.end(from);
          from = from.mixingFrom;
        }
      }

      current.trackTime += currentDelta;
    }

    this.queue.drain();
  }
  /** Returns true when all mixing from entries are complete. */
  ;

  _proto.updateMixingFrom = function updateMixingFrom(to, delta) {
    var from = to.mixingFrom;
    if (!from) return true;
    var finished = this.updateMixingFrom(from, delta);
    from.animationLast = from.nextAnimationLast;
    from.trackLast = from.nextTrackLast; // Require mixTime > 0 to ensure the mixing from entry was applied at least once.

    if (to.mixTime > 0 && to.mixTime >= to.mixDuration) {
      // Require totalAlpha == 0 to ensure mixing is complete, unless mixDuration == 0 (the transition is a single frame).
      if (from.totalAlpha == 0 || to.mixDuration == 0) {
        to.mixingFrom = from.mixingFrom;
        if (from.mixingFrom) from.mixingFrom.mixingTo = to;
        to.interruptAlpha = from.interruptAlpha;
        this.queue.end(from);
      }

      return finished;
    }

    from.trackTime += delta * from.timeScale;
    to.mixTime += delta;
    return false;
  }
  /** Poses the skeleton using the track entry animations. There are no side effects other than invoking listeners, so the
   * animation state can be applied to multiple skeletons to pose them identically.
   * @returns True if any animations were applied. */
  ;

  _proto.apply = function apply(skeleton) {
    if (!skeleton) throw new Error("skeleton cannot be null.");
    if (this.animationsChanged) this._animationsChanged();
    var events = this.events;
    var tracks = this.tracks;
    var applied = false;

    for (var _i = 0, _n = tracks.length; _i < _n; _i++) {
      var current = tracks[_i];
      if (!current || current.delay > 0) continue;
      applied = true;
      var blend = _i == 0 ? _Animation.MixBlend.first : current.mixBlend; // Apply mixing from entries first.

      var mix = current.alpha;
      if (current.mixingFrom) mix *= this.applyMixingFrom(current, skeleton, blend);else if (current.trackTime >= current.trackEnd && !current.next) mix = 0; // Apply current entry.

      var animationLast = current.animationLast,
          animationTime = current.getAnimationTime(),
          applyTime = animationTime;
      var applyEvents = events;

      if (current.reverse) {
        applyTime = current.animation.duration - applyTime;
        applyEvents = null;
      }

      var timelines = current.animation.timelines;
      var timelineCount = timelines.length;

      if (_i == 0 && mix == 1 || blend == _Animation.MixBlend.add) {
        for (var ii = 0; ii < timelineCount; ii++) {
          // Fixes issue #302 on IOS9 where mix, blend sometimes became undefined and caused assets
          // to sometimes stop rendering when using color correction, as their RGBA values become NaN.
          // (https://github.com/pixijs/pixi-spine/issues/302)
          _Utils.Utils.webkit602BugfixHelper(mix, blend);

          var timeline = timelines[ii];
          if (timeline instanceof _Animation.AttachmentTimeline) this.applyAttachmentTimeline(timeline, skeleton, applyTime, blend, true);else timeline.apply(skeleton, animationLast, applyTime, applyEvents, mix, blend, _Animation.MixDirection.mixIn);
        }
      } else {
        var timelineMode = current.timelineMode;
        var firstFrame = current.timelinesRotation.length != timelineCount << 1;
        if (firstFrame) current.timelinesRotation.length = timelineCount << 1;

        for (var _ii = 0; _ii < timelineCount; _ii++) {
          var _timeline = timelines[_ii];
          var timelineBlend = timelineMode[_ii] == SUBSEQUENT ? blend : _Animation.MixBlend.setup;

          if (_timeline instanceof _Animation.RotateTimeline) {
            this.applyRotateTimeline(_timeline, skeleton, applyTime, mix, timelineBlend, current.timelinesRotation, _ii << 1, firstFrame);
          } else if (_timeline instanceof _Animation.AttachmentTimeline) {
            this.applyAttachmentTimeline(_timeline, skeleton, applyTime, blend, true);
          } else {
            // This fixes the WebKit 602 specific issue described at http://esotericsoftware.com/forum/iOS-10-disappearing-graphics-10109
            _Utils.Utils.webkit602BugfixHelper(mix, blend);

            _timeline.apply(skeleton, animationLast, applyTime, applyEvents, mix, timelineBlend, _Animation.MixDirection.mixIn);
          }
        }
      }

      this.queueEvents(current, animationTime);
      events.length = 0;
      current.nextAnimationLast = animationTime;
      current.nextTrackLast = current.trackTime;
    } // Set slots attachments to the setup pose, if needed. This occurs if an animation that is mixing out sets attachments so
    // subsequent timelines see any deform, but the subsequent timelines don't set an attachment (eg they are also mixing out or
    // the time is before the first key).


    var setupState = this.unkeyedState + SETUP;
    var slots = skeleton.slots;

    for (var i = 0, n = skeleton.slots.length; i < n; i++) {
      var slot = slots[i];

      if (slot.attachmentState == setupState) {
        var attachmentName = slot.data.attachmentName;
        slot.setAttachment(!attachmentName ? null : skeleton.getAttachment(slot.data.index, attachmentName));
      }
    }

    this.unkeyedState += 2; // Increasing after each use avoids the need to reset attachmentState for every slot.

    this.queue.drain();
    return applied;
  };

  _proto.applyMixingFrom = function applyMixingFrom(to, skeleton, blend) {
    var from = to.mixingFrom;
    if (from.mixingFrom) this.applyMixingFrom(from, skeleton, blend);
    var mix = 0;

    if (to.mixDuration == 0) {
      // Single frame mix to undo mixingFrom changes.
      mix = 1;
      if (blend == _Animation.MixBlend.first) blend = _Animation.MixBlend.setup;
    } else {
      mix = to.mixTime / to.mixDuration;
      if (mix > 1) mix = 1;
      if (blend != _Animation.MixBlend.first) blend = from.mixBlend;
    }

    var attachments = mix < from.attachmentThreshold,
        drawOrder = mix < from.drawOrderThreshold;
    var timelines = from.animation.timelines;
    var timelineCount = timelines.length;
    var alphaHold = from.alpha * to.interruptAlpha,
        alphaMix = alphaHold * (1 - mix);
    var animationLast = from.animationLast,
        animationTime = from.getAnimationTime(),
        applyTime = animationTime;
    var events = null;
    if (from.reverse) applyTime = from.animation.duration - applyTime;else if (mix < from.eventThreshold) events = this.events;

    if (blend == _Animation.MixBlend.add) {
      for (var i = 0; i < timelineCount; i++) {
        timelines[i].apply(skeleton, animationLast, applyTime, events, alphaMix, blend, _Animation.MixDirection.mixOut);
      }
    } else {
      var timelineMode = from.timelineMode;
      var timelineHoldMix = from.timelineHoldMix;
      var firstFrame = from.timelinesRotation.length != timelineCount << 1;
      if (firstFrame) from.timelinesRotation.length = timelineCount << 1;
      from.totalAlpha = 0;

      for (var _i2 = 0; _i2 < timelineCount; _i2++) {
        var timeline = timelines[_i2];
        var direction = _Animation.MixDirection.mixOut;
        var timelineBlend = void 0;
        var alpha = 0;

        switch (timelineMode[_i2]) {
          case SUBSEQUENT:
            if (!drawOrder && timeline instanceof _Animation.DrawOrderTimeline) continue;
            timelineBlend = blend;
            alpha = alphaMix;
            break;

          case FIRST:
            timelineBlend = _Animation.MixBlend.setup;
            alpha = alphaMix;
            break;

          case HOLD_SUBSEQUENT:
            timelineBlend = blend;
            alpha = alphaHold;
            break;

          case HOLD_FIRST:
            timelineBlend = _Animation.MixBlend.setup;
            alpha = alphaHold;
            break;

          default:
            timelineBlend = _Animation.MixBlend.setup;
            var holdMix = timelineHoldMix[_i2];
            alpha = alphaHold * Math.max(0, 1 - holdMix.mixTime / holdMix.mixDuration);
            break;
        }

        from.totalAlpha += alpha;
        if (timeline instanceof _Animation.RotateTimeline) this.applyRotateTimeline(timeline, skeleton, applyTime, alpha, timelineBlend, from.timelinesRotation, _i2 << 1, firstFrame);else if (timeline instanceof _Animation.AttachmentTimeline) this.applyAttachmentTimeline(timeline, skeleton, applyTime, timelineBlend, attachments);else {
          // This fixes the WebKit 602 specific issue described at http://esotericsoftware.com/forum/iOS-10-disappearing-graphics-10109
          _Utils.Utils.webkit602BugfixHelper(alpha, blend);

          if (drawOrder && timeline instanceof _Animation.DrawOrderTimeline && timelineBlend == _Animation.MixBlend.setup) direction = _Animation.MixDirection.mixIn;
          timeline.apply(skeleton, animationLast, applyTime, events, alpha, timelineBlend, direction);
        }
      }
    }

    if (to.mixDuration > 0) this.queueEvents(from, animationTime);
    this.events.length = 0;
    from.nextAnimationLast = animationTime;
    from.nextTrackLast = from.trackTime;
    return mix;
  };

  _proto.applyAttachmentTimeline = function applyAttachmentTimeline(timeline, skeleton, time, blend, attachments) {
    var slot = skeleton.slots[timeline.slotIndex];
    if (!slot.bone.active) return;

    if (time < timeline.frames[0]) {
      // Time is before first frame.
      if (blend == _Animation.MixBlend.setup || blend == _Animation.MixBlend.first) this.setAttachment(skeleton, slot, slot.data.attachmentName, attachments);
    } else this.setAttachment(skeleton, slot, timeline.attachmentNames[_Animation.Timeline.search1(timeline.frames, time)], attachments); // If an attachment wasn't set (ie before the first frame or attachments is false), set the setup attachment later.


    if (slot.attachmentState <= this.unkeyedState) slot.attachmentState = this.unkeyedState + SETUP;
  };

  _proto.setAttachment = function setAttachment(skeleton, slot, attachmentName, attachments) {
    slot.setAttachment(!attachmentName ? null : skeleton.getAttachment(slot.data.index, attachmentName));
    if (attachments) slot.attachmentState = this.unkeyedState + CURRENT;
  };

  _proto.applyRotateTimeline = function applyRotateTimeline(timeline, skeleton, time, alpha, blend, timelinesRotation, i, firstFrame) {
    if (firstFrame) timelinesRotation[i] = 0;

    if (alpha == 1) {
      timeline.apply(skeleton, 0, time, null, 1, blend, _Animation.MixDirection.mixIn);
      return;
    }

    var bone = skeleton.bones[timeline.boneIndex];
    if (!bone.active) return;
    var frames = timeline.frames;
    var r1 = 0,
        r2 = 0;

    if (time < frames[0]) {
      switch (blend) {
        case _Animation.MixBlend.setup:
          bone.rotation = bone.data.rotation;

        default:
          return;

        case _Animation.MixBlend.first:
          r1 = bone.rotation;
          r2 = bone.data.rotation;
      }
    } else {
      r1 = blend == _Animation.MixBlend.setup ? bone.data.rotation : bone.rotation;
      r2 = bone.data.rotation + timeline.getCurveValue(time);
    } // Mix between rotations using the direction of the shortest route on the first frame while detecting crosses.


    var total = 0,
        diff = r2 - r1;
    diff -= (16384 - (16384.499999999996 - diff / 360 | 0)) * 360;

    if (diff == 0) {
      total = timelinesRotation[i];
    } else {
      var lastTotal = 0,
          lastDiff = 0;

      if (firstFrame) {
        lastTotal = 0;
        lastDiff = diff;
      } else {
        lastTotal = timelinesRotation[i]; // Angle and direction of mix, including loops.

        lastDiff = timelinesRotation[i + 1]; // Difference between bones.
      }

      var current = diff > 0,
          dir = lastTotal >= 0; // Detect cross at 0 (not 180).

      if (_Utils.MathUtils.signum(lastDiff) != _Utils.MathUtils.signum(diff) && Math.abs(lastDiff) <= 90) {
        // A cross after a 360 rotation is a loop.
        if (Math.abs(lastTotal) > 180) lastTotal += 360 * _Utils.MathUtils.signum(lastTotal);
        dir = current;
      }

      total = diff + lastTotal - lastTotal % 360; // Store loops as part of lastTotal.

      if (dir != current) total += 360 * _Utils.MathUtils.signum(lastTotal);
      timelinesRotation[i] = total;
    }

    timelinesRotation[i + 1] = diff;
    bone.rotation = r1 + total * alpha;
  };

  _proto.queueEvents = function queueEvents(entry, animationTime) {
    var animationStart = entry.animationStart,
        animationEnd = entry.animationEnd;
    var duration = animationEnd - animationStart;
    var trackLastWrapped = entry.trackLast % duration; // Queue events before complete.

    var events = this.events;
    var i = 0,
        n = events.length;

    for (; i < n; i++) {
      var event = events[i];
      if (event.time < trackLastWrapped) break;
      if (event.time > animationEnd) continue; // Discard events outside animation start/end.

      this.queue.event(entry, event);
    } // Queue complete if completed a loop iteration or the animation.


    var complete = false;
    if (entry.loop) complete = duration == 0 || trackLastWrapped > entry.trackTime % duration;else complete = animationTime >= animationEnd && entry.animationLast < animationEnd;
    if (complete) this.queue.complete(entry); // Queue events after complete.

    for (; i < n; i++) {
      var _event = events[i];
      if (_event.time < animationStart) continue; // Discard events outside animation start/end.

      this.queue.event(entry, _event);
    }
  }
  /** Removes all animations from all tracks, leaving skeletons in their current pose.
   *
   * It may be desired to use {@link AnimationState#setEmptyAnimation()} to mix the skeletons back to the setup pose,
   * rather than leaving them in their current pose. */
  ;

  _proto.clearTracks = function clearTracks() {
    var oldDrainDisabled = this.queue.drainDisabled;
    this.queue.drainDisabled = true;

    for (var i = 0, n = this.tracks.length; i < n; i++) {
      this.clearTrack(i);
    }

    this.tracks.length = 0;
    this.queue.drainDisabled = oldDrainDisabled;
    this.queue.drain();
  }
  /** Removes all animations from the track, leaving skeletons in their current pose.
   *
   * It may be desired to use {@link AnimationState#setEmptyAnimation()} to mix the skeletons back to the setup pose,
   * rather than leaving them in their current pose. */
  ;

  _proto.clearTrack = function clearTrack(trackIndex) {
    if (trackIndex >= this.tracks.length) return;
    var current = this.tracks[trackIndex];
    if (!current) return;
    this.queue.end(current);
    this.clearNext(current);
    var entry = current;

    while (true) {
      var from = entry.mixingFrom;
      if (!from) break;
      this.queue.end(from);
      entry.mixingFrom = null;
      entry.mixingTo = null;
      entry = from;
    }

    this.tracks[current.trackIndex] = null;
    this.queue.drain();
  };

  _proto.setCurrent = function setCurrent(index, current, interrupt) {
    var from = this.expandToIndex(index);
    this.tracks[index] = current;
    current.previous = null;

    if (from) {
      if (interrupt) this.queue.interrupt(from);
      current.mixingFrom = from;
      from.mixingTo = current;
      current.mixTime = 0; // Store the interrupted mix percentage.

      if (from.mixingFrom && from.mixDuration > 0) current.interruptAlpha *= Math.min(1, from.mixTime / from.mixDuration);
      from.timelinesRotation.length = 0; // Reset rotation for mixing out, in case entry was mixed in.
    }

    this.queue.start(current);
  }
  /** Sets an animation by name.
    *
    * See {@link #setAnimationWith()}. */
  ;

  _proto.setAnimation = function setAnimation(trackIndex, animationName, loop) {
    if (loop === void 0) {
      loop = false;
    }

    var animation = this.data.skeletonData.findAnimation(animationName);
    if (!animation) throw new Error("Animation not found: " + animationName);
    return this.setAnimationWith(trackIndex, animation, loop);
  }
  /** Sets the current animation for a track, discarding any queued animations. If the formerly current track entry was never
   * applied to a skeleton, it is replaced (not mixed from).
   * @param loop If true, the animation will repeat. If false it will not, instead its last frame is applied if played beyond its
   *           duration. In either case {@link TrackEntry#trackEnd} determines when the track is cleared.
   * @returns A track entry to allow further customization of animation playback. References to the track entry must not be kept
   *         after the {@link AnimationStateListener#dispose()} event occurs. */
  ;

  _proto.setAnimationWith = function setAnimationWith(trackIndex, animation, loop) {
    if (loop === void 0) {
      loop = false;
    }

    if (!animation) throw new Error("animation cannot be null.");
    var interrupt = true;
    var current = this.expandToIndex(trackIndex);

    if (current) {
      if (current.nextTrackLast == -1) {
        // Don't mix from an entry that was never applied.
        this.tracks[trackIndex] = current.mixingFrom;
        this.queue.interrupt(current);
        this.queue.end(current);
        this.clearNext(current);
        current = current.mixingFrom;
        interrupt = false;
      } else this.clearNext(current);
    }

    var entry = this.trackEntry(trackIndex, animation, loop, current);
    this.setCurrent(trackIndex, entry, interrupt);
    this.queue.drain();
    return entry;
  }
  /** Queues an animation by name.
   *
   * See {@link #addAnimationWith()}. */
  ;

  _proto.addAnimation = function addAnimation(trackIndex, animationName, loop, delay) {
    if (loop === void 0) {
      loop = false;
    }

    if (delay === void 0) {
      delay = 0;
    }

    var animation = this.data.skeletonData.findAnimation(animationName);
    if (!animation) throw new Error("Animation not found: " + animationName);
    return this.addAnimationWith(trackIndex, animation, loop, delay);
  }
  /** Adds an animation to be played after the current or last queued animation for a track. If the track is empty, it is
   * equivalent to calling {@link #setAnimationWith()}.
   * @param delay If > 0, sets {@link TrackEntry#delay}. If <= 0, the delay set is the duration of the previous track entry
   *           minus any mix duration (from the {@link AnimationStateData}) plus the specified `delay` (ie the mix
   *           ends at (`delay` = 0) or before (`delay` < 0) the previous track entry duration). If the
   *           previous entry is looping, its next loop completion is used instead of its duration.
   * @returns A track entry to allow further customization of animation playback. References to the track entry must not be kept
   *         after the {@link AnimationStateListener#dispose()} event occurs. */
  ;

  _proto.addAnimationWith = function addAnimationWith(trackIndex, animation, loop, delay) {
    if (loop === void 0) {
      loop = false;
    }

    if (delay === void 0) {
      delay = 0;
    }

    if (!animation) throw new Error("animation cannot be null.");
    var last = this.expandToIndex(trackIndex);

    if (last) {
      while (last.next) {
        last = last.next;
      }
    }

    var entry = this.trackEntry(trackIndex, animation, loop, last);

    if (!last) {
      this.setCurrent(trackIndex, entry, true);
      this.queue.drain();
    } else {
      last.next = entry;
      entry.previous = last;
      if (delay <= 0) delay += last.getTrackComplete() - entry.mixDuration;
    }

    entry.delay = delay;
    return entry;
  }
  /** Sets an empty animation for a track, discarding any queued animations, and sets the track entry's
   * {@link TrackEntry#mixduration}. An empty animation has no timelines and serves as a placeholder for mixing in or out.
   *
   * Mixing out is done by setting an empty animation with a mix duration using either {@link #setEmptyAnimation()},
   * {@link #setEmptyAnimations()}, or {@link #addEmptyAnimation()}. Mixing to an empty animation causes
   * the previous animation to be applied less and less over the mix duration. Properties keyed in the previous animation
   * transition to the value from lower tracks or to the setup pose value if no lower tracks key the property. A mix duration of
   * 0 still mixes out over one frame.
   *
   * Mixing in is done by first setting an empty animation, then adding an animation using
   * {@link #addAnimation()} and on the returned track entry, set the
   * {@link TrackEntry#setMixDuration()}. Mixing from an empty animation causes the new animation to be applied more and
   * more over the mix duration. Properties keyed in the new animation transition from the value from lower tracks or from the
   * setup pose value if no lower tracks key the property to the value keyed in the new animation. */
  ;

  _proto.setEmptyAnimation = function setEmptyAnimation(trackIndex, mixDuration) {
    if (mixDuration === void 0) {
      mixDuration = 0;
    }

    var entry = this.setAnimationWith(trackIndex, AnimationState.emptyAnimation(), false);
    entry.mixDuration = mixDuration;
    entry.trackEnd = mixDuration;
    return entry;
  }
  /** Adds an empty animation to be played after the current or last queued animation for a track, and sets the track entry's
   * {@link TrackEntry#mixDuration}. If the track is empty, it is equivalent to calling
   * {@link #setEmptyAnimation()}.
   *
   * See {@link #setEmptyAnimation()}.
   * @param delay If > 0, sets {@link TrackEntry#delay}. If <= 0, the delay set is the duration of the previous track entry
   *           minus any mix duration plus the specified `delay` (ie the mix ends at (`delay` = 0) or
   *           before (`delay` < 0) the previous track entry duration). If the previous entry is looping, its next
   *           loop completion is used instead of its duration.
   * @return A track entry to allow further customization of animation playback. References to the track entry must not be kept
   *         after the {@link AnimationStateListener#dispose()} event occurs. */
  ;

  _proto.addEmptyAnimation = function addEmptyAnimation(trackIndex, mixDuration, delay) {
    if (mixDuration === void 0) {
      mixDuration = 0;
    }

    if (delay === void 0) {
      delay = 0;
    }

    var entry = this.addAnimationWith(trackIndex, AnimationState.emptyAnimation(), false, delay);
    if (delay <= 0) entry.delay += entry.mixDuration - mixDuration;
    entry.mixDuration = mixDuration;
    entry.trackEnd = mixDuration;
    return entry;
  }
  /** Sets an empty animation for every track, discarding any queued animations, and mixes to it over the specified mix
    * duration. */
  ;

  _proto.setEmptyAnimations = function setEmptyAnimations(mixDuration) {
    if (mixDuration === void 0) {
      mixDuration = 0;
    }

    var oldDrainDisabled = this.queue.drainDisabled;
    this.queue.drainDisabled = true;

    for (var i = 0, n = this.tracks.length; i < n; i++) {
      var current = this.tracks[i];
      if (current) this.setEmptyAnimation(current.trackIndex, mixDuration);
    }

    this.queue.drainDisabled = oldDrainDisabled;
    this.queue.drain();
  };

  _proto.expandToIndex = function expandToIndex(index) {
    if (index < this.tracks.length) return this.tracks[index];

    _Utils.Utils.ensureArrayCapacity(this.tracks, index + 1, null);

    this.tracks.length = index + 1;
    return null;
  }
  /** @param last May be null. */
  ;

  _proto.trackEntry = function trackEntry(trackIndex, animation, loop, last) {
    var entry = this.trackEntryPool.obtain();
    entry.reset();
    entry.trackIndex = trackIndex;
    entry.animation = animation;
    entry.loop = loop;
    entry.holdPrevious = false;
    entry.eventThreshold = 0;
    entry.attachmentThreshold = 0;
    entry.drawOrderThreshold = 0;
    entry.animationStart = 0;
    entry.animationEnd = animation.duration;
    entry.animationLast = -1;
    entry.nextAnimationLast = -1;
    entry.delay = 0;
    entry.trackTime = 0;
    entry.trackLast = -1;
    entry.nextTrackLast = -1;
    entry.trackEnd = Number.MAX_VALUE;
    entry.timeScale = 1;
    entry.alpha = 1;
    entry.interruptAlpha = 1;
    entry.mixTime = 0;
    entry.mixDuration = !last ? 0 : this.data.getMix(last.animation, animation);
    entry.mixBlend = _Animation.MixBlend.replace;
    return entry;
  }
  /** Removes the {@link TrackEntry#getNext() next entry} and all entries after it for the specified entry. */
  ;

  _proto.clearNext = function clearNext(entry) {
    var next = entry.next;

    while (next) {
      this.queue.dispose(next);
      next = next.next;
    }

    entry.next = null;
  };

  _proto._animationsChanged = function _animationsChanged() {
    this.animationsChanged = false;
    this.propertyIDs.clear();
    var tracks = this.tracks;

    for (var i = 0, n = tracks.length; i < n; i++) {
      var entry = tracks[i];
      if (!entry) continue;

      while (entry.mixingFrom) {
        entry = entry.mixingFrom;
      }

      do {
        if (!entry.mixingTo || entry.mixBlend != _Animation.MixBlend.add) this.computeHold(entry);
        entry = entry.mixingTo;
      } while (entry);
    }
  };

  _proto.computeHold = function computeHold(entry) {
    var to = entry.mixingTo;
    var timelines = entry.animation.timelines;
    var timelinesCount = entry.animation.timelines.length;
    var timelineMode = entry.timelineMode;
    timelineMode.length = timelinesCount;
    var timelineHoldMix = entry.timelineHoldMix;
    timelineHoldMix.length = 0;
    var propertyIDs = this.propertyIDs;

    if (to && to.holdPrevious) {
      for (var i = 0; i < timelinesCount; i++) {
        timelineMode[i] = propertyIDs.addAll(timelines[i].getPropertyIds()) ? HOLD_FIRST : HOLD_SUBSEQUENT;
      }

      return;
    }

    outer: for (var _i3 = 0; _i3 < timelinesCount; _i3++) {
      var timeline = timelines[_i3];
      var ids = timeline.getPropertyIds();
      if (!propertyIDs.addAll(ids)) timelineMode[_i3] = SUBSEQUENT;else if (!to || timeline instanceof _Animation.AttachmentTimeline || timeline instanceof _Animation.DrawOrderTimeline || timeline instanceof _Animation.EventTimeline || !to.animation.hasTimeline(ids)) {
        timelineMode[_i3] = FIRST;
      } else {
        for (var next = to.mixingTo; next; next = next.mixingTo) {
          if (next.animation.hasTimeline(ids)) continue;

          if (entry.mixDuration > 0) {
            timelineMode[_i3] = HOLD_MIX;
            timelineHoldMix[_i3] = next;
            continue outer;
          }

          break;
        }

        timelineMode[_i3] = HOLD_FIRST;
      }
    }
  }
  /** Returns the track entry for the animation currently playing on the track, or null if no animation is currently playing. */
  ;

  _proto.getCurrent = function getCurrent(trackIndex) {
    if (trackIndex >= this.tracks.length) return null;
    return this.tracks[trackIndex];
  }
  /** Adds a listener to receive events for all track entries. */
  ;

  _proto.addListener = function addListener(listener) {
    if (!listener) throw new Error("listener cannot be null.");
    this.listeners.push(listener);
  }
  /** Removes the listener added with {@link #addListener()}. */
  ;

  _proto.removeListener = function removeListener(listener) {
    var index = this.listeners.indexOf(listener);
    if (index >= 0) this.listeners.splice(index, 1);
  }
  /** Removes all listeners added with {@link #addListener()}. */
  ;

  _proto.clearListeners = function clearListeners() {
    this.listeners.length = 0;
  }
  /** Discards all listener notifications that have not yet been delivered. This can be useful to call from an
   * {@link AnimationStateListener} when it is known that further notifications that may have been already queued for delivery
   * are not wanted because new animations are being set. */
  ;

  _proto.clearListenerNotifications = function clearListenerNotifications() {
    this.queue.clear();
  };

  return AnimationState;
}();
/** Stores settings and other state for the playback of an animation on an {@link AnimationState} track.
 *
 * References to a track entry must not be kept after the {@link AnimationStateListener#dispose()} event occurs. */


exports.AnimationState = AnimationState;

var TrackEntry = /*#__PURE__*/function () {
  function TrackEntry() {
    /** The animation to apply for this track entry. */
    this.animation = null;
    this.previous = null;
    /** The animation queued to start after this animation, or null. `next` makes up a linked list. */

    this.next = null;
    /** The track entry for the previous animation when mixing from the previous animation to this animation, or null if no
     * mixing is currently occuring. When mixing from multiple animations, `mixingFrom` makes up a linked list. */

    this.mixingFrom = null;
    /** The track entry for the next animation when mixing from this animation to the next animation, or null if no mixing is
     * currently occuring. When mixing to multiple animations, `mixingTo` makes up a linked list. */

    this.mixingTo = null;
    /** The listener for events generated by this track entry, or null.
     *
     * A track entry returned from {@link AnimationState#setAnimation()} is already the current animation
     * for the track, so the track entry listener {@link AnimationStateListener#start()} will not be called. */

    this.listener = null;
    /** The index of the track where this track entry is either current or queued.
     *
     * See {@link AnimationState#getCurrent()}. */

    this.trackIndex = 0;
    /** If true, the animation will repeat. If false it will not, instead its last frame is applied if played beyond its
     * duration. */

    this.loop = false;
    /** If true, when mixing from the previous animation to this animation, the previous animation is applied as normal instead
     * of being mixed out.
     *
     * When mixing between animations that key the same property, if a lower track also keys that property then the value will
     * briefly dip toward the lower track value during the mix. This happens because the first animation mixes from 100% to 0%
     * while the second animation mixes from 0% to 100%. Setting `holdPrevious` to true applies the first animation
     * at 100% during the mix so the lower track value is overwritten. Such dipping does not occur on the lowest track which
     * keys the property, only when a higher track also keys the property.
     *
     * Snapping will occur if `holdPrevious` is true and this animation does not key all the same properties as the
     * previous animation. */

    this.holdPrevious = false;
    this.reverse = false;
    /** When the mix percentage ({@link #mixTime} / {@link #mixDuration}) is less than the
     * `eventThreshold`, event timelines are applied while this animation is being mixed out. Defaults to 0, so event
     * timelines are not applied while this animation is being mixed out. */

    this.eventThreshold = 0;
    /** When the mix percentage ({@link #mixtime} / {@link #mixDuration}) is less than the
     * `attachmentThreshold`, attachment timelines are applied while this animation is being mixed out. Defaults to
     * 0, so attachment timelines are not applied while this animation is being mixed out. */

    this.attachmentThreshold = 0;
    /** When the mix percentage ({@link #mixTime} / {@link #mixDuration}) is less than the
     * `drawOrderThreshold`, draw order timelines are applied while this animation is being mixed out. Defaults to 0,
     * so draw order timelines are not applied while this animation is being mixed out. */

    this.drawOrderThreshold = 0;
    /** Seconds when this animation starts, both initially and after looping. Defaults to 0.
     *
     * When changing the `animationStart` time, it often makes sense to set {@link #animationLast} to the same
     * value to prevent timeline keys before the start time from triggering. */

    this.animationStart = 0;
    /** Seconds for the last frame of this animation. Non-looping animations won't play past this time. Looping animations will
     * loop back to {@link #animationStart} at this time. Defaults to the animation {@link Animation#duration}. */

    this.animationEnd = 0;
    /** The time in seconds this animation was last applied. Some timelines use this for one-time triggers. Eg, when this
     * animation is applied, event timelines will fire all events between the `animationLast` time (exclusive) and
     * `animationTime` (inclusive). Defaults to -1 to ensure triggers on frame 0 happen the first time this animation
     * is applied. */

    this.animationLast = 0;
    this.nextAnimationLast = 0;
    /** Seconds to postpone playing the animation. When this track entry is the current track entry, `delay`
     * postpones incrementing the {@link #trackTime}. When this track entry is queued, `delay` is the time from
     * the start of the previous animation to when this track entry will become the current track entry (ie when the previous
     * track entry {@link TrackEntry#trackTime} >= this track entry's `delay`).
     *
     * {@link #timeScale} affects the delay. */

    this.delay = 0;
    /** Current time in seconds this track entry has been the current track entry. The track time determines
     * {@link #animationTime}. The track time can be set to start the animation at a time other than 0, without affecting
     * looping. */

    this.trackTime = 0;
    this.trackLast = 0;
    this.nextTrackLast = 0;
    /** The track time in seconds when this animation will be removed from the track. Defaults to the highest possible float
     * value, meaning the animation will be applied until a new animation is set or the track is cleared. If the track end time
     * is reached, no other animations are queued for playback, and mixing from any previous animations is complete, then the
     * properties keyed by the animation are set to the setup pose and the track is cleared.
     *
     * It may be desired to use {@link AnimationState#addEmptyAnimation()} rather than have the animation
     * abruptly cease being applied. */

    this.trackEnd = 0;
    /** Multiplier for the delta time when this track entry is updated, causing time for this animation to pass slower or
     * faster. Defaults to 1.
     *
     * {@link #mixTime} is not affected by track entry time scale, so {@link #mixDuration} may need to be adjusted to
     * match the animation speed.
     *
     * When using {@link AnimationState#addAnimation()} with a `delay` <= 0, note the
     * {@link #delay} is set using the mix duration from the {@link AnimationStateData}, assuming time scale to be 1. If
     * the time scale is not 1, the delay may need to be adjusted.
     *
     * See AnimationState {@link AnimationState#timeScale} for affecting all animations. */

    this.timeScale = 0;
    /** Values < 1 mix this animation with the skeleton's current pose (usually the pose resulting from lower tracks). Defaults
     * to 1, which overwrites the skeleton's current pose with this animation.
     *
     * Typically track 0 is used to completely pose the skeleton, then alpha is used on higher tracks. It doesn't make sense to
     * use alpha on track 0 if the skeleton pose is from the last frame render. */

    this.alpha = 0;
    /** Seconds from 0 to the {@link #getMixDuration()} when mixing from the previous animation to this animation. May be
     * slightly more than `mixDuration` when the mix is complete. */

    this.mixTime = 0;
    /** Seconds for mixing from the previous animation to this animation. Defaults to the value provided by AnimationStateData
     * {@link AnimationStateData#getMix()} based on the animation before this animation (if any).
     *
     * A mix duration of 0 still mixes out over one frame to provide the track entry being mixed out a chance to revert the
     * properties it was animating.
     *
     * The `mixDuration` can be set manually rather than use the value from
     * {@link AnimationStateData#getMix()}. In that case, the `mixDuration` can be set for a new
     * track entry only before {@link AnimationState#update(float)} is first called.
     *
     * When using {@link AnimationState#addAnimation()} with a `delay` <= 0, note the
     * {@link #delay} is set using the mix duration from the {@link AnimationStateData}, not a mix duration set
     * afterward. */

    this.mixDuration = 0;
    this.interruptAlpha = 0;
    this.totalAlpha = 0;
    /** Controls how properties keyed in the animation are mixed with lower tracks. Defaults to {@link MixBlend#replace}, which
     * replaces the values from the lower tracks with the animation values. {@link MixBlend#add} adds the animation values to
     * the values from the lower tracks.
     *
     * The `mixBlend` can be set for a new track entry only before {@link AnimationState#apply()} is first
     * called. */

    this.mixBlend = _Animation.MixBlend.replace;
    this.timelineMode = new Array();
    this.timelineHoldMix = new Array();
    this.timelinesRotation = new Array();
  }

  var _proto2 = TrackEntry.prototype;

  _proto2.reset = function reset() {
    this.next = null;
    this.previous = null;
    this.mixingFrom = null;
    this.mixingTo = null;
    this.animation = null;
    this.listener = null;
    this.timelineMode.length = 0;
    this.timelineHoldMix.length = 0;
    this.timelinesRotation.length = 0;
  }
  /** Uses {@link #trackTime} to compute the `animationTime`, which is between {@link #animationStart}
   * and {@link #animationEnd}. When the `trackTime` is 0, the `animationTime` is equal to the
   * `animationStart` time. */
  ;

  _proto2.getAnimationTime = function getAnimationTime() {
    if (this.loop) {
      var duration = this.animationEnd - this.animationStart;
      if (duration == 0) return this.animationStart;
      return this.trackTime % duration + this.animationStart;
    }

    return Math.min(this.trackTime + this.animationStart, this.animationEnd);
  };

  _proto2.setAnimationLast = function setAnimationLast(animationLast) {
    this.animationLast = animationLast;
    this.nextAnimationLast = animationLast;
  }
  /** Returns true if at least one loop has been completed.
   *
   * See {@link AnimationStateListener#complete()}. */
  ;

  _proto2.isComplete = function isComplete() {
    return this.trackTime >= this.animationEnd - this.animationStart;
  }
  /** Resets the rotation directions for mixing this entry's rotate timelines. This can be useful to avoid bones rotating the
   * long way around when using {@link #alpha} and starting animations on other tracks.
   *
   * Mixing with {@link MixBlend#replace} involves finding a rotation between two others, which has two possible solutions:
   * the short way or the long way around. The two rotations likely change over time, so which direction is the short or long
   * way also changes. If the short way was always chosen, bones would flip to the other side when that direction became the
   * long way. TrackEntry chooses the short way the first time it is applied and remembers that direction. */
  ;

  _proto2.resetRotationDirections = function resetRotationDirections() {
    this.timelinesRotation.length = 0;
  };

  _proto2.getTrackComplete = function getTrackComplete() {
    var duration = this.animationEnd - this.animationStart;

    if (duration != 0) {
      if (this.loop) return duration * (1 + (this.trackTime / duration | 0)); // Completion of next loop.

      if (this.trackTime < duration) return duration; // Before duration.
    }

    return this.trackTime; // Next update.
  };

  return TrackEntry;
}();

exports.TrackEntry = TrackEntry;

var EventQueue = /*#__PURE__*/function () {
  function EventQueue(animState) {
    this.objects = [];
    this.drainDisabled = false;
    this.animState = null;
    this.animState = animState;
  }

  var _proto3 = EventQueue.prototype;

  _proto3.start = function start(entry) {
    this.objects.push(EventType.start);
    this.objects.push(entry);
    this.animState.animationsChanged = true;
  };

  _proto3.interrupt = function interrupt(entry) {
    this.objects.push(EventType.interrupt);
    this.objects.push(entry);
  };

  _proto3.end = function end(entry) {
    this.objects.push(EventType.end);
    this.objects.push(entry);
    this.animState.animationsChanged = true;
  };

  _proto3.dispose = function dispose(entry) {
    this.objects.push(EventType.dispose);
    this.objects.push(entry);
  };

  _proto3.complete = function complete(entry) {
    this.objects.push(EventType.complete);
    this.objects.push(entry);
  };

  _proto3.event = function event(entry, _event2) {
    this.objects.push(EventType.event);
    this.objects.push(entry);
    this.objects.push(_event2);
  };

  _proto3.drain = function drain() {
    if (this.drainDisabled) return;
    this.drainDisabled = true;
    var objects = this.objects;
    var listeners = this.animState.listeners;

    for (var i = 0; i < objects.length; i += 2) {
      var type = objects[i];
      var entry = objects[i + 1];

      switch (type) {
        case EventType.start:
          if (entry.listener && entry.listener.start) entry.listener.start(entry);

          for (var ii = 0; ii < listeners.length; ii++) {
            if (listeners[ii].start) listeners[ii].start(entry);
          }

          break;

        case EventType.interrupt:
          if (entry.listener && entry.listener.interrupt) entry.listener.interrupt(entry);

          for (var _ii2 = 0; _ii2 < listeners.length; _ii2++) {
            if (listeners[_ii2].interrupt) listeners[_ii2].interrupt(entry);
          }

          break;

        case EventType.end:
          if (entry.listener && entry.listener.end) entry.listener.end(entry);

          for (var _ii3 = 0; _ii3 < listeners.length; _ii3++) {
            if (listeners[_ii3].end) listeners[_ii3].end(entry);
          }

        // Fall through.

        case EventType.dispose:
          if (entry.listener && entry.listener.dispose) entry.listener.dispose(entry);

          for (var _ii4 = 0; _ii4 < listeners.length; _ii4++) {
            if (listeners[_ii4].dispose) listeners[_ii4].dispose(entry);
          }

          this.animState.trackEntryPool.free(entry);
          break;

        case EventType.complete:
          if (entry.listener && entry.listener.complete) entry.listener.complete(entry);

          for (var _ii5 = 0; _ii5 < listeners.length; _ii5++) {
            if (listeners[_ii5].complete) listeners[_ii5].complete(entry);
          }

          break;

        case EventType.event:
          var event = objects[i++ + 2];
          if (entry.listener && entry.listener.event) entry.listener.event(entry, event);

          for (var _ii6 = 0; _ii6 < listeners.length; _ii6++) {
            if (listeners[_ii6].event) listeners[_ii6].event(entry, event);
          }

          break;
      }
    }

    this.clear();
    this.drainDisabled = false;
  };

  _proto3.clear = function clear() {
    this.objects.length = 0;
  };

  return EventQueue;
}();

exports.EventQueue = EventQueue;
var EventType;
exports.EventType = EventType;

(function (EventType) {
  EventType[EventType["start"] = 0] = "start";
  EventType[EventType["interrupt"] = 1] = "interrupt";
  EventType[EventType["end"] = 2] = "end";
  EventType[EventType["dispose"] = 3] = "dispose";
  EventType[EventType["complete"] = 4] = "complete";
  EventType[EventType["event"] = 5] = "event";
})(EventType || (exports.EventType = EventType = {}));

var AnimationStateAdapter = /*#__PURE__*/function () {
  function AnimationStateAdapter() {}

  var _proto4 = AnimationStateAdapter.prototype;

  _proto4.start = function start(entry) {};

  _proto4.interrupt = function interrupt(entry) {};

  _proto4.end = function end(entry) {};

  _proto4.dispose = function dispose(entry) {};

  _proto4.complete = function complete(entry) {};

  _proto4.event = function event(entry, _event3) {};

  return AnimationStateAdapter;
}();
/** 1. A previously applied timeline has set this property.
 *
 * Result: Mix from the current pose to the timeline pose. */


exports.AnimationStateAdapter = AnimationStateAdapter;
var SUBSEQUENT = 0;
/** 1. This is the first timeline to set this property.
 * 2. The next track entry applied after this one does not have a timeline to set this property.
 *
 * Result: Mix from the setup pose to the timeline pose. */

exports.SUBSEQUENT = SUBSEQUENT;
var FIRST = 1;
/** 1) A previously applied timeline has set this property.<br>
 * 2) The next track entry to be applied does have a timeline to set this property.<br>
 * 3) The next track entry after that one does not have a timeline to set this property.<br>
 * Result: Mix from the current pose to the timeline pose, but do not mix out. This avoids "dipping" when crossfading
 * animations that key the same property. A subsequent timeline will set this property using a mix. */

exports.FIRST = FIRST;
var HOLD_SUBSEQUENT = 2;
/** 1) This is the first timeline to set this property.<br>
 * 2) The next track entry to be applied does have a timeline to set this property.<br>
 * 3) The next track entry after that one does not have a timeline to set this property.<br>
 * Result: Mix from the setup pose to the timeline pose, but do not mix out. This avoids "dipping" when crossfading animations
 * that key the same property. A subsequent timeline will set this property using a mix. */

exports.HOLD_SUBSEQUENT = HOLD_SUBSEQUENT;
var HOLD_FIRST = 3;
/** 1. This is the first timeline to set this property.
 * 2. The next track entry to be applied does have a timeline to set this property.
 * 3. The next track entry after that one does have a timeline to set this property.
 * 4. timelineHoldMix stores the first subsequent track entry that does not have a timeline to set this property.
 *
 * Result: The same as HOLD except the mix percentage from the timelineHoldMix track entry is used. This handles when more than
 * 2 track entries in a row have a timeline that sets the same property.
 *
 * Eg, A -> B -> C -> D where A, B, and C have a timeline setting same property, but D does not. When A is applied, to avoid
 * "dipping" A is not mixed out, however D (the first entry that doesn't set the property) mixing in is used to mix out A
 * (which affects B and C). Without using D to mix out, A would be applied fully until mixing completes, then snap into
 * place. */

exports.HOLD_FIRST = HOLD_FIRST;
var HOLD_MIX = 4;
exports.HOLD_MIX = HOLD_MIX;
var SETUP = 1;
exports.SETUP = SETUP;
var CURRENT = 2;
exports.CURRENT = CURRENT;
var _emptyAnimation = null;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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